import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { DEFAULT_INTERVIEW_PROMPT, DEFAULT_SUMMARY_PROMPT } from "../constants/prompts";

export default function Dashboard(){
  const navigate = useNavigate();
  const { userId } = useParams();
  const [interviews, setInterviews] = useState([]);
  const [jobTitle, setJobTitle] = useState("");
  const [skills, setSkills] = useState("");
  const [instructions, setInstructions] = useState("");
  const [scoringInstructions, setScoringInstructions] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("interview"); // "interview" or "summary"
  const [interviewPrompt, setInterviewPrompt] = useState(DEFAULT_INTERVIEW_PROMPT);
  const [summaryPrompt, setSummaryPrompt] = useState(DEFAULT_SUMMARY_PROMPT);



  useEffect(() => {
    if (!userId) return;
    axios.get(`${process.env.REACT_APP_API_URL}/api/interviews/${userId}`)
      .then(res => setInterviews(res.data))
      .catch(()=>setInterviews([]));
  }, [userId]);

  function buildPrompt(template, data) {
    return template.replace(/{{(.*?)}}/g, (_, key) => {
      const value = data[key.trim()];
      if (Array.isArray(value)) return value.join(", ");
      return value !== undefined ? value : "";
    });
  }

  const createInterview = async () => {
    try {
      const data = {
        job_title: jobTitle,
        skills: skills.split(",").map(s => s.trim()).filter(Boolean),
        instructions,
        scoring_instructions: scoringInstructions
      };

      // Build resolved prompts
      const resolvedInterviewPrompt = buildPrompt(DEFAULT_INTERVIEW_PROMPT, data);
      const resolvedSummaryPrompt = buildPrompt(DEFAULT_SUMMARY_PROMPT, data);
      console.log("Resolved Interview Prompt:", resolvedInterviewPrompt);
      console.log("Resolved Summary Prompt:", resolvedSummaryPrompt);

      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/interviews`, {
        interviewer_id: userId,
        job_title: jobTitle,
        skills: data.skills,
        instructions,
        scoring_instructions: scoringInstructions,
        interview_prompt: resolvedInterviewPrompt || interviewPrompt,
        summary_prompt:resolvedSummaryPrompt || summaryPrompt
      });
      alert(`Interview link: https://interviewai-qr0y.onrender.com${res.data.link}`);
      // refresh
      const list = await axios.get(`${process.env.REACT_APP_API_URL}/api/interviews/${userId}`);
      setInterviews(list.data);
      setJobTitle(""); setSkills(""); setInstructions("");
    } catch (err) {
      alert("Failed to create interview");
    }
  };

  return (
    <div className="container">
      <h1 style={{marginBottom:8}}>Dashboard</h1>

      <div className="card grid cols-2">
        <div>
          <h3>Create Interview</h3>
          <div style={{position:"relative", marginBottom:12}}>
  <input
    className="input"
    placeholder="Job Title"
    value={jobTitle}
    onChange={e=>setJobTitle(e.target.value)}
    style={{paddingRight:"28px"}}
  />
  <span className="help-icon">?</span>
  <div className="tooltip">
    <strong>Job Title</strong>
    <hr />
    <p>This is the role you’re hiring for.</p>
    <p><em>Example:</em> Frontend Developer, Data Analyst, Project Manager</p>
  </div>
</div>

<div style={{position:"relative", marginBottom:12}}>
  <input
    className="input"
    placeholder="Skills (comma separated)"
    value={skills}
    onChange={e=>setSkills(e.target.value)}
    style={{paddingRight:"28px"}}
  />
  <span className="help-icon">?</span>
  <div className="tooltip">
    <strong>Skills</strong>
    <hr />
    <p>List the key skills required for the role, separated by commas.</p>
    <p><em>Example:</em> React, CSS, APIs, Communication</p>
  </div>
</div>

<div style={{position:"relative", marginBottom:12}}>
  <textarea
    className="input"
    placeholder="Instructions"
    value={instructions}
    onChange={e=>setInstructions(e.target.value)}
    style={{paddingRight:"28px",minHeight: "100px",resize:"vertical" }}
  />
  <span className="help-icon">?</span>
  <div className="tooltip">
    <strong>Instructions</strong>
    <hr />
    <p>Provide clear guidelines for how the interview should be conducted. 
       This helps the AI interviewer know what to focus on.</p>
    <p>Include things like:</p>
    <ul>
      <li>Specific topics to cover (e.g., coding, teamwork, leadership)</li>
      <li>Evaluation focus (e.g., problem‑solving, communication skills)</li>
    </ul>
    <p style={{marginTop:"8px"}}><em>Example:</em></p>
    <p style={{marginLeft:"16px"}}>
      “Start with basic JavaScript questions, then move to React hooks and state management. 
      Ask one behavioral question about teamwork. 
      End with a short coding exercise (array manipulation). 
      Keep tone supportive and give hints if the candidate struggles.”
    </p>
  </div>
</div>

<div style={{position:"relative", marginBottom:12}}>
  <textarea
    className="input"
    placeholder="Scoring Instructions"
    value={scoringInstructions}
    onChange={e=>setScoringInstructions(e.target.value)}
    style={{paddingRight:"28px",minHeight:"100px",resize:"vertical"}}
  />
  <span className="help-icon">?</span>
  <div className="tooltip">
    <strong>Scoring Instructions</strong>
    <hr />
    <p>Provide clear guidelines for how the interview should be scored.</p>
    <p>Include things like:</p>
    <ul>
      <li>Criteria for evaluating answers (accuracy, clarity, depth)</li>
      <li>Weighting of skills (e.g., technical 70%, communication 30%)</li>
      <li>Numeric scale or rubric (0–100, 1–5 stars, etc.)</li>
    </ul>
    <p style={{marginTop:"8px"}}><em>Example:</em></p>
    <p style={{marginLeft:"16px"}}>
      “Score coding accuracy out of 50, communication clarity out of 30, and teamwork examples out of 20. 
      Deduct points for incomplete answers or lack of reasoning.”
    </p>
  </div>
</div>


         
          <div style={{marginTop:12}}>
            <button 
              className="btn secondary" 
              style={{marginLeft:8, marginRight:8}}
              onClick={() => setShowModal(true)}
            >
              Advanced Settings
            </button>

            {showModal && (
  <div className="modal-overlay">
    <div className="modal">
      <h3>Advanced Settings</h3>

      {/* Tabs */}
      <div className="tabs">
        <button 
          className={activeTab === "interview" ? "tab active" : "tab"} 
          onClick={() => setActiveTab("interview")}
        >
          Interview Prompt
        </button>
        <button 
          className={activeTab === "summary" ? "tab active" : "tab"} 
          onClick={() => setActiveTab("summary")}
        >
          Summary Prompt
        </button>
      </div>

      {/* Textarea fills modal */}
      <div style={{flex:1, marginTop:12}}>
        {activeTab === "interview" ? (
          <textarea
            className="input"
            value={interviewPrompt}
            onChange={e=>setInterviewPrompt(e.target.value)}
            style={{width:"100%", height:"300px", resize:"vertical"}}
          />
        ) : (
          <textarea
            className="input"
            value={summaryPrompt}
            onChange={e=>setSummaryPrompt(e.target.value)}
            style={{width:"100%", height:"300px", resize:"vertical"}}
          />
        )}
      </div>

      {/* Actions */}
      <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:12}}>
        <button className="btn secondary" onClick={()=>setShowModal(false)}>Cancel</button>
        <button className="btn" onClick={()=>{
          setShowModal(false);
          // include interviewPrompt and summaryPrompt in createInterview payload
        }}>Save</button>
      </div>
    </div>
  </div>
)}



            <button className="btn" onClick={createInterview}>Create Interview</button>
          </div>
        </div>

        <div>
          <h3>Quick Stats</h3>
          <div className="card-compact" style={{marginTop:8}}>
            <div>
              <div className="small">Total interviews</div>
              <div style={{fontWeight:700,fontSize:20}}>{interviews.length}</div>
            </div>
            <div>
              <div className="small">Completed sessions</div>
              <div style={{fontWeight:700,fontSize:20}}>
                {interviews.reduce((acc,i)=>acc + (i.completed_count||0),0)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <h3 style={{marginTop:18}}>My Interviews</h3>
      <div className="grid" style={{marginTop:8}}>
        {interviews.length === 0 && <div className="card">No interviews yet.</div>}
        {interviews.map(i => (
          <div key={i._id} className="card" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontWeight:700,fontSize:16}}>{i.job_title}</div>
              <div className="small" style={{marginTop:6}}>
                Link: 
                <a 
                  href={`https://interviewai-qr0y.onrender.com/interviewee/${i.link_id}`} 
                  style={{ color: "var(--accent)" }}
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  https://interviewai-qr0y.onrender.com/interviewee/{i.link_id}
                </a>
              </div>
              <div className="small" style={{marginTop:6}}>Skills: {i.skills.join(", ")}</div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8,alignItems:"flex-end"}}>
            <div className="small">Completed: <strong>{i.completed_count || 0}</strong></div>
            <div style={{display:"flex",gap:8}}>
              <button className="btn secondary" onClick={()=>navigate(`/interviews/${i._id}/sessions`)}>View Sessions</button>
              <button className="btn danger" onClick={async ()=>{
                if (!window.confirm("Delete this interview? All sessions will be removed.")) return;
                try {
                  await axios.delete(`${process.env.REACT_APP_API_URL}/api/interviews/${i._id}`);
                  setInterviews(prev => prev.filter(intv => intv._id !== i._id));
                } catch {
                  alert("Failed to delete interview");
                }
              }}>Delete</button>
            </div>
          </div>

          </div>
        ))}
      </div>
    </div>
  );
}
